import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { tenantApi } from '@/lib/api/client';
import { useAuth } from '@/lib/auth';

const membersKey = ['tenant', 'members'] as const;

export function TenantSharing() {
  const { activeTenantRole } = useAuth();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: membersKey,
    queryFn: () => tenantApi.getMembers(),
    enabled: activeTenantRole === 'owner',
  });

  const addMember = useMutation({
    mutationFn: (memberEmail: string) => tenantApi.addMember(memberEmail, 'viewer'),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: membersKey });
      setEmail('');
      toast.success(`${result.member.email} can now view this journal`);
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to add member'),
  });

  const removeMember = useMutation({
    mutationFn: (id: string) => tenantApi.removeMember(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membersKey });
      toast.success('Access removed');
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to remove member'),
  });

  // Viewers don't manage sharing
  if (activeTenantRole !== 'owner') return null;

  const members = data?.members ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sharing</CardTitle>
        <CardDescription>
          Grant other people read-only access to this journal. They sign in with
          their own account and can view everything, but change nothing.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-col sm:flex-row gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim()) addMember.mutate(email.trim());
          }}
        >
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@example.com"
            className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
          <Button type="submit" isLoading={addMember.isPending} disabled={!email.trim()}>
            Add Viewer
          </Button>
        </form>

        {isLoading ? null : members.length === 0 ? (
          <p className="text-sm text-gray-400">No one else has access.</p>
        ) : (
          <div className="space-y-2">
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-sm text-gray-900 truncate">{member.email}</span>
                  <Badge variant={member.role === 'owner' ? 'success' : 'default'} size="sm">
                    {member.role === 'owner' ? 'Owner' : 'Read-only'}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (confirm(`Remove ${member.email}'s access?`)) {
                      removeMember.mutate(member.id);
                    }
                  }}
                  isLoading={removeMember.isPending}
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
